import { useContext, useEffect, useRef, useState } from 'react'
import { Accordion, Button, TextField, Flex, Box, Text } from 'src/componentv2'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'

interface Props {
    open: boolean
    onToggle: (o: boolean) => void
    onSaveAndNext?: () => void
}

interface CodeSearchFieldProps {
    placeholder: string
    fetcher: (search: string) => Promise<{ items?: any[] } | false | null>
    extractCode: (item: any) => string
    multi?: boolean
    values: string[]
    onChange: (values: string[]) => void
}

const CodeSearchField: React.FC<CodeSearchFieldProps> = ({
                                                             placeholder, fetcher, extractCode, multi = false, values, onChange,
                                                         }) => {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<string[]>([])
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        if (!open) return
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
            setLoading(true)
            const res = await fetcher(query)
            const items = (res && (res as any).items) || []
            setResults(items.map(extractCode))
            setLoading(false)
        }, 300)
        return () => clearTimeout(debounceRef.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, open])

    const selectCode = (code: string) => {
        if (multi) {
            if (!values.includes(code)) onChange([...values, code])
            setQuery('')
        } else {
            onChange([code])
            setOpen(false)
        }
    }

    const removeCode = (code: string) => onChange(values.filter((v) => v !== code))

    return (
        <Box className="business-logic__code-search">
            {multi && values.length > 0 && (
                <Flex gap="0.8" wrap="wrap" topMargin="0" bottomMargin="0.8">
                    {values.map((code) => (
                        <span key={code} className="business-logic__code-chip">
            {code}
                            <button type="button" onClick={() => removeCode(code)} aria-label={`Remove ${code}`}>
                ×
            </button>
        </span>
                    ))}
                </Flex>
            )}

            {(!multi ? values.length === 0 : true) && (
                <div className="business-logic__code-search-input">
                    <TextField
                        placeholder={placeholder}
                        value={query}
                        onChange={(v: string) => { setQuery(v); setOpen(true) }}
                        onFocus={() => setOpen(true)}
                    />
                    {open && (
                        <div className="business-logic__code-dropdown">
                            {loading && <Text fontSize="1.3" color="#98A2B3">Searching…</Text>}
                            {!loading && results.length === 0 && (
                                <Text fontSize="1.3" color="#98A2B3">No matching codes</Text>
                            )}
                            {!loading && results.map((code) => (
                                <button
                                    type="button"
                                    key={code}
                                    className="business-logic__code-option"
                                    onClick={() => selectCode(code)}
                                >
                                    {code}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!multi && values.length > 0 && (
                <Flex items="center" gap="0.8">
                    <span className="business-logic__code-chip">
                        {values[0]}
                        <button type="button" onClick={() => onChange([])} aria-label="Clear selection">
                            ×
                        </button>
                    </span>
                </Flex>
            )}
        </Box>
    )
}

export const AccountOpeningSettings: React.FC<Props> = ({ open, onToggle, onSaveAndNext }) => {
    const { getInstitutionProfile, getProducts, getAccountOfficers, updateAccountOpeningPolicy } =
        useContext(ConfigurationApiContext)

    const [defaultProductCode, setDefaultProductCode] = useState<string[]>([])
    const [moreProductCodes, setMoreProductCodes] = useState<string[]>([])
    const [officerCode, setOfficerCode] = useState<string[]>([])
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        (async () => {
            const profile = await getInstitutionProfile()
            if (!profile) return
            if (profile.defaultAccountOpeningProductCode) {
                setDefaultProductCode([profile.defaultAccountOpeningProductCode])
            }
            // Note: GET returns this as `allowedAccountProductCodes`, but the PUT payload
            // expects `useMultipleOnboardingProductCodes` — real naming mismatch between
            // the two endpoints, not a typo on my end. Confirm with backend if this changes.
            if (profile.allowedAccountProductCodes) {
                setMoreProductCodes(
                    String(profile.allowedAccountProductCodes).split(',').map((c: string) => c.trim()).filter(Boolean),
                )
            }
            if (profile.defaultAccountOfficerCode) {
                setOfficerCode([profile.defaultAccountOfficerCode])
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchProducts = (search: string) =>
        getProducts({ PageLimit: 10, PageNumber: 1, FetchAll: false, search })

    const fetchOfficers = (search: string) =>
        getAccountOfficers({ PageLimit: 10, PageNumber: 1, FetchAll: false, search })

    const handleSave = async () => {
        setSaving(true)

        const result = await updateAccountOpeningPolicy({
            defaultAccountOpeningProductCode: defaultProductCode[0] ?? '',
            useMultipleOnboardingProductCodes: moreProductCodes.join(','),
            defaultAccountOfficerCode: officerCode[0] ?? '',
        })

        setSaving(false)
        if (result !== false && result !== null) {
            onSaveAndNext?.()
        }
    }

    return (
        <Accordion title="Account Opening settings" open={open} onToggle={onToggle}>
            <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                Default Onboarding Account Opening Product code
            </Text>
            <CodeSearchField
                placeholder="Search product code…"
                fetcher={fetchProducts}
                extractCode={(item) => item.productCode}
                values={defaultProductCode}
                onChange={setDefaultProductCode}
            />

            <Box topMargin="2">
                <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                    Select More Onboarding Account Opening Product code (optional)
                </Text>
                <CodeSearchField
                    placeholder="Search product code…"
                    fetcher={fetchProducts}
                    extractCode={(item) => item.productCode}
                    multi
                    values={moreProductCodes}
                    onChange={setMoreProductCodes}
                />
            </Box>

            <Box topMargin="2">
                <Text fontSize="1.4" style={{ fontFamily: 'Gilmer Medium' }} bottomMargin="0.8">
                    Default Account Officer code
                </Text>
                <CodeSearchField
                    placeholder="Search account officer code…"
                    fetcher={fetchOfficers}
                    extractCode={(item) => item.code}
                    values={officerCode}
                    onChange={setOfficerCode}
                />
            </Box>

            <Flex justify="flex-end" topMargin="2.4" className="accordion__footer">
                <Button variant="ghost" size="sm" disabled={saving} onClick={handleSave}>
                    {saving ? 'Saving…' : 'Save Changes'}
                </Button>
            </Flex>
        </Accordion>
    )
}

export default AccountOpeningSettings